import {computed, ref, readonly, watch, type ComputedRef, type Ref} from 'vue';
import {storeToRefs} from 'pinia';
import {useRouter, type NavigationGuardNext, type RouteLocationNormalized, type RouteLocationRaw} from 'vue-router';

import {useUserStore, type UserData} from '@/ts/store/user-store';
import {ContractStatus} from '@/ts/types/dto/user-dto';
import {pagesPath} from '@/ts/router/pages-path';
import {isLegalConsentPending} from '@/ts/composables/pure/use-legal-consent-gate';
import {sessionStorage} from '@/ts/instances/session-storage';

/**
 * Contract-expiry locking (IPD-2661).
 *
 * Once a voucher / license / subscription expires the backend drops
 * `activeContract` to `null` while `lastContract` keeps the now-EXPIRED contract.
 * Such users must be locked out of every section except the renewal flow.
 *
 * IMPORTANT: this is a UX layer, NOT the security boundary. The backend is the
 * authority — it must reject content reads for expired contracts (it already
 * returns `NO_ACTIVE_LICENSES` on enrollment and content reads). This guard
 * only spares the user from loading pages they can't use and surfaces the
 * renewal dialog; it must never be relied on to protect data.
 *
 * The lock is enforced in two complementary places:
 *  1. `contractExpiryNavigationGuard` — a `router.beforeEach` hook that blocks
 *     locked routes BEFORE the target page mounts (no content flash, no stray
 *     data fetches) for every navigation where the user store is already
 *     populated (i.e. all in-app navigation).
 *  2. `useContractExpiryWatch` — a reactive watch in the app shell that catches
 *     the two cases a `beforeEach` cannot: the first hard-load (store is empty
 *     until `/me` resolves) and a contract expiring mid-session.
 *
 * Route exemption is declared via `meta.allowExpired` on the route record, not
 * pattern-matched here, so adding a new always-reachable route is a one-line,
 * discoverable change next to the route definition.
 *
 * A pending legal consent (terms or privacy policy) takes precedence over
 * expiry (IPD-3536). The backend refuses every call but `/me` and the consent
 * endpoints until it is given, renewal included, so sending such a user to
 * renew strands them. Both places send them to the consent flow first; once it
 * is given, the next navigation applies the expiry lock as before.
 */

/** The renewal destination — the only place an expired user can act. */
const RENEWAL_LOCATION: RouteLocationRaw = {path: pagesPath.account.subscription};

/** Where a user whose legal consent is pending has to go before anything else. */
const CONSENT_LOCATION: RouteLocationRaw = {path: pagesPath.main.consentRequired};

/** True for the consent flow itself, which must never redirect to itself. */
function isConsentRoute(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta.consentFlow === true);
}

/** True for routes behind sign-in, the only ones the consent gate applies to. */
function isAuthRoute(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta.auth === true);
}

/** True for the few routes that stay usable while the consent is pending (see `allowPendingConsent`). */
function allowsPendingConsent(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta.allowPendingConsent === true);
}

/** True when the consent gate has to redirect this navigation. */
function mustGiveConsentFirst(userData: UserData | null | undefined, route: RouteLocationNormalized): boolean {
  return isLegalConsentPending(userData) &&
    isAuthRoute(route) &&
    !isConsentRoute(route) &&
    !allowsPendingConsent(route);
}

/** Sends the user to the consent flow, remembering where they were headed for when they consent. */
function consentLocationFrom(route: RouteLocationNormalized): RouteLocationRaw {
  sessionStorage.consentReturnPath = route.fullPath;
  return CONSENT_LOCATION;
}

/**
 * The remembered destination, used up by the first navigation that does not belong to the consent
 * flow, so it never goes stale. Leaving the flow with the consent given turns it into the target:
 * whatever the consent page navigated to is replaced by where the gate interrupted the user. Leaving
 * without it (Back to the profile, which stays reachable) keeps it for when they do consent.
 */
function takeConsentReturnPath(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
  userData: UserData | null | undefined,
): string | null {
  if (isConsentRoute(to) || (isConsentRoute(from) && isLegalConsentPending(userData))) {
    return null;
  }
  const returnPath = sessionStorage.consentReturnPath;
  sessionStorage.consentReturnPath = null;
  if (!isConsentRoute(from) || !isAuthRoute(to)) {
    return null;
  }
  return returnPath?.startsWith('/') && returnPath !== to.fullPath ? returnPath : null;
}

/** True when the route (or any matched ancestor) opts out of the lock. */
function isExemptRoute(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta.allowExpired === true);
}

/**
 * Set whenever the guard blocks a locked navigation; consumed (cleared) by the
 * subscription tab when it shows the Content Locked dialog. A pending flag rather
 * than a URL query param or an API error code, so the guard and the dialog stay
 * decoupled. It's a flag (not an event) so the very first lock — fired by the
 * watch backstop BEFORE the subscription page mounts on a hard-load — is still
 * observed once the page's `{immediate}` watcher runs.
 */
const lockNoticePending = ref(false);

function notifyLocked(): void {
  lockNoticePending.value = true;
}

/**
 * Pure expiry signal. Locks only on a genuine expiry: no currently-active
 * contract AND the most recent contract is EXPIRED. A user who never held a
 * contract (`lastContract` null/absent) is left to the signup / purchase flow,
 * not bounced here. Covers voucher, gifted and direct-payment uniformly because
 * the backend drops `activeContract` for all of them on lapse.
 *
 * Account-lifecycle state (`userData.userState`: inactive / anonymized) is
 * deliberately NOT considered here — that is a separate concern from plan expiry,
 * handled by the auth/session layer, not this guard.
 */
export function isContractExpired(userData: UserData | null | undefined): boolean {
  const lastContract = userData?.lastContract;
  if (!lastContract) {
    return false;
  }
  return !userData?.activeContract && lastContract.status === ContractStatus.EXPIRED;
}

/** Lock-notice signal for the renewal page: read the pending flag, consume on show. */
export function useLockNotice(): {lockNoticePending: Readonly<Ref<boolean>>; consumeLockNotice(): void} {
  return {
    lockNoticePending: readonly(lockNoticePending),
    consumeLockNotice(): void {
      lockNoticePending.value = false;
    },
  };
}

/**
 * `router.beforeEach` guard. Returns a user who has just consented to where the
 * consent gate interrupted them. Redirects to the consent flow when a legal
 * consent is pending, else to the renewal flow when the authenticated user is
 * expired and the target route isn't exempt; otherwise allows navigation.
 * Reads the user store directly so it works for any in-app navigation (store
 * already populated) and blocks before the target page mounts. Register once on
 * the main-app router only (the admin app has no contracts). Uses `next`-style to
 * match the existing i18n router guard.
 */
export function contractExpiryNavigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void {
  const userStore = useUserStore();
  const returnPath = takeConsentReturnPath(from, to, userStore.userData);
  if (returnPath) {
    // A navigation of its own, so the gates below still apply to it.
    next(returnPath);
    return;
  }
  if (mustGiveConsentFirst(userStore.userData, to)) {
    next(consentLocationFrom(to));
    return;
  }
  if (isContractExpired(userStore.userData) && !isExemptRoute(to)) {
    notifyLocked();
    next(RENEWAL_LOCATION);
    return;
  }
  next();
}

/**
 * Reactive backstop for the cases `beforeEach` can't see: the first hard-load
 * (store empty when the guard first ran) and a mid-session expiry. Applies the
 * same order as the guard: consent first, then expiry. Call once
 * from the authenticated app shell. Returns `isContractExpired` for the shell to
 * use if needed.
 */
export function useContractExpiryWatch(): {isContractExpired: ComputedRef<boolean>} {
  const router = useRouter();
  const {userData} = storeToRefs(useUserStore());
  const expired = computed((): boolean => isContractExpired(userData.value));
  const consentPending = computed((): boolean => isLegalConsentPending(userData.value));

  watch(
    [consentPending, expired],
    ([isConsentPending, isExpired]) => {
      const route = router.currentRoute.value;
      if (isConsentPending) {
        if (mustGiveConsentFirst(userData.value, route)) {
          void router.replace(consentLocationFrom(route));
        }
        return;
      }
      if (isExpired && !isExemptRoute(route)) {
        notifyLocked();
        void router.replace(RENEWAL_LOCATION);
      }
    },
    {immediate: true},
  );

  return {isContractExpired: expired};
}
